/**
 * MagicLogin — worker/admin auto-login via `/m/:token` URL.
 * Backend mints these from POST /api/users/{user_id}/magic-link.
 * The exchange only happens on a real tap (never on page load) so email
 * security scanners and SMS link-previewers can't burn the one-time token.
 */
import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Warning, ArrowsClockwise, SignIn } from "@phosphor-icons/react";
import { LogoLockup } from "@/components/Logo";

export default function MagicLogin() {
  const { token } = useParams();
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const fired = useRef(false);
  const API = process.env.REACT_APP_BACKEND_URL;

  const go = async () => {
    if (fired.current) return;
    fired.current = true;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/auth/magic/${token}`, { method: "POST", credentials: "include" });
      if (!r.ok) { setErr((await r.json().catch(() => ({}))).detail || "Magic link failed"); fired.current = false; return; }
      const data = await r.json();
      if (data.access_token) localStorage.setItem("access_token", data.access_token);
      const role = data.user?.role || "worker";
      window.dispatchEvent(new Event("auth-changed"));
      window.location.replace(role === "admin" ? "/admin" : "/worker");
    } catch (e) { setErr("Network error — check your connection and try again"); fired.current = false; }
    finally { setBusy(false); }
  };

  if (err) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border-2 border-slate-200 rounded-lg p-6 text-center" data-testid="magic-error">
          <Warning weight="fill" size={40} className="mx-auto text-amber-500"/>
          <h1 className="mt-3 font-black text-xl">Can't sign you in</h1>
          <p className="mt-2 text-sm text-slate-600">{err}</p>
          <p className="mt-4 text-xs text-slate-500">Ask the office for a fresh magic link.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-[#f4f4f5] flex items-center justify-center p-6">
      <div className="max-w-sm w-full bg-white border-2 border-[#0B1C2D] shadow-[8px_8px_0px_#0B1C2D] p-8 text-center" data-testid="magic-landing">
        <div className="flex justify-center"><LogoLockup height={56}/></div>
        <h1 className="mt-6 font-display font-black text-2xl text-[#0B1C2D]">Your sign-in link is ready</h1>
        <p className="mt-2 text-sm text-slate-600">Tap below to open FieldRadar. No password needed.</p>
        <button onClick={go} disabled={busy} data-testid="magic-continue-btn"
          className="mt-6 w-full h-14 bg-[#14B8A6] hover:bg-[#0fa392] disabled:opacity-60 text-white font-black uppercase tracking-widest text-sm flex items-center justify-center gap-2 transition-colors">
          {busy ? <ArrowsClockwise size={20} className="animate-spin"/> : <SignIn weight="bold" size={20}/>}
          {busy ? "Signing you in…" : "Continue to FieldRadar"}
        </button>
        <p className="mt-4 text-[11px] text-slate-400">This link is personal — don't forward it.</p>
      </div>
    </div>
  );
}
